// Pure, side-effect-free item logic. Extracted from useItems so it can be unit
// tested directly and reused. No React, no Supabase here.

import type { Item, Category } from './supabase'
import { STALE_BASKET_MS } from './constants'

/**
 * Merge a realtime row change into the current list, deduping by id.
 * This is the logic that the duplicate-item bug lived in: an INSERT whose id
 * already exists (the optimistic row) must REPLACE, not append.
 */
export function applyRealtimeChange(
  current: Item[],
  change: { type: 'INSERT' | 'UPDATE' | 'DELETE'; row: Item },
): Item[] {
  switch (change.type) {
    case 'INSERT': {
      const exists = current.some(i => i.id === change.row.id)
      return exists
        ? current.map(i => (i.id === change.row.id ? change.row : i))
        : [change.row, ...current]
    }
    case 'UPDATE':
      if (change.row.deleted_at) return current.filter(i => i.id !== change.row.id)
      // A live row this list doesn't hold is one that was just restored
      // (Undo on another phone clears deleted_at): it comes back.
      return current.some(i => i.id === change.row.id)
        ? current.map(i => (i.id === change.row.id ? change.row : i))
        : [change.row, ...current]
    case 'DELETE':
      return current.filter(i => i.id !== change.row.id)
  }
}

/** Optimistically toggle an item's done flag by id (returns a new array); `by` is who picked it. */
export function toggleDone(items: Item[], id: string, done: boolean, at: string = new Date().toISOString(), by: string | null = null): Item[] {
  // done_at too: the basket is ordered by it, and "Last: …" names the newest.
  // done_by as the database will set it (046), so a Finish right after has it.
  return items.map(i => (i.id === id ? { ...i, done, done_at: done ? at : null, done_by: done ? by : null } : i))
}

/** Insert an optimistic item at the front (newest first). */
export function insertOptimistic(items: Item[], item: Item): Item[] {
  return [item, ...items]
}

/**
 * Optimistically apply an edit by id. With `restore`, the edit also brings a
 * done item back to the active list — that's the "tap a done suggestion's name,
 * change the quantity, add it" flow, which has to be one write so the two
 * changes can't race each other under last-write-wins.
 */
export function applyUpdate(
  items: Item[],
  vars: { id: string; name: string; quantity: string | null; category_id: string | null; note?: string | null; restore?: boolean },
): Item[] {
  return items.map(i =>
    i.id === vars.id
      ? {
          ...i,
          name: vars.name,
          quantity: vars.quantity,
          category_id: vars.category_id,
          ...(vars.note !== undefined ? { note: vars.note } : {}),
          ...(vars.restore ? { done: false, done_at: null } : {}),
        }
      : i,
  )
}

// ── The kit's list model (docs/matlist-kit/reference/src/listModel.ts) ───────
// The kit expresses the list as one reducer; here the React Query cache is the
// state, so each of its actions is one of the helpers above or below:
// add → insertOptimistic, edit → applyUpdate, toBasket/putBack → toggleDone,
// remove → filter, restore → restoreAt, finishTrip → useItems.finishItems.

export interface ActiveSection {
  category: Category | null   // null = the "Other"/uncategorised bucket
  items: Item[]               // active (not done) items only; done ones are in the basket
}

/**
 * Active items by category, in `sort_order`, "Other" last. The phone hides
 * empty sections (ux/03 CategorySection); the planning layout passes
 * `includeEmpty` because every category is always shown there (ux/02).
 */
export function activeSections(items: Item[], categories: Category[], includeEmpty = false): ActiveSection[] {
  const known = new Set(categories.map(c => c.id))
  const by = new Map<string | null, Item[]>()
  for (const item of items) {
    if (item.done) continue
    const key = item.category_id && known.has(item.category_id) ? item.category_id : null
    by.set(key, [...(by.get(key) ?? []), item])
  }
  const sections: ActiveSection[] = [...categories]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map(category => ({ category, items: by.get(category.id) ?? [] }))
    .filter(s => includeEmpty || s.items.length > 0)
  const other = by.get(null) ?? []
  if (other.length) sections.push({ category: null, items: other })
  return sections
}

/** Items in the basket: ticked, waiting for Finish trip. Most recent first. */
export function basketOf(items: Item[]): Item[] {
  return items
    .filter(i => i.done)
    .sort((a, b) => (b.done_at ?? '').localeCompare(a.done_at ?? ''))
}

/**
 * Basket rows from an earlier trip nobody finished: ticked more than
 * STALE_BASKET_MS ago, or with no tick time at all. They belong in the history
 * (Running low, quick add's "From before"), not in today's basket.
 */
export function staleBasket(items: Item[], now: Date): Item[] {
  const cutoff = now.getTime() - STALE_BASKET_MS
  return items.filter(i => i.done && (!i.done_at || new Date(i.done_at).getTime() < cutoff))
}

/** Trip progress: how much of the list is already in the basket. */
export function progress(items: Item[]): { inBasket: number; total: number; ratio: number } {
  const total = items.length
  const inBasket = items.filter(i => i.done).length
  return { inBasket, total, ratio: total ? inBasket / total : 0 }
}

/**
 * Undo after a delete: put the row back where it was. Idempotent — a realtime
 * echo of the restore may already have brought it back.
 */
export function restoreAt(items: Item[], item: Item, index: number): Item[] {
  if (items.some(i => i.id === item.id)) return items
  const next = [...items]
  next.splice(Math.max(0, Math.min(index, next.length)), 0, item)
  return next
}

/** Trim, collapse runs of spaces, capitalise the first letter: "  oat   milk " → "Oat milk". */
export function normalizeName(raw: string): string {
  const s = raw.trim().replace(/\s+/g, ' ')
  return s ? s[0].toUpperCase() + s.slice(1) : ''
}
